"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { GlyphRankedSession } from "./glyph-ranked-session";

import type { GlyphSessionApi } from "./glyph-account";

/** A game identity lease never populates the external asset-wallet connection. */
export function useGlyphRankedSession(
  session: GlyphSessionApi | null,
  signedOut: boolean,
  sessionEpoch: number,
  selectedWallet: string | null
) {
  const [controller] = useState(() => new GlyphRankedSession());
  const account =
    !signedOut && session?.authenticated ? session.privyUserId : null;
  const [binding, setBinding] = useState({
    account,
    sessionEpoch,
    selectedWallet,
    generation: 0,
  });
  if (
    binding.account !== account ||
    binding.sessionEpoch !== sessionEpoch ||
    binding.selectedWallet !== selectedWallet
  )
    setBinding({
      account,
      sessionEpoch,
      selectedWallet,
      generation: binding.generation + 1,
    });
  const [now, setNow] = useState(Date.now);
  const revoke = useCallback(() => controller.revoke(), [controller]);
  // Retire this identity's writes before the next account/wallet can use the seat.
  useLayoutEffect(() => {
    return () => {
      void revoke().catch(() => undefined);
    };
  }, [binding.generation, revoke]);
  const seat = useQuery({
    queryKey: [
      "glyph-ranked-seat",
      account,
      sessionEpoch,
      selectedWallet,
      binding.generation,
    ],
    queryFn: () =>
      session
        ? controller.bind(session, selectedWallet ?? undefined)
        : Promise.resolve(null),
    enabled: Boolean(account),
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  });
  const expiresAt = seat.data?.expiresAt;
  // Hide an expired lease even when renewal fails or a background tab resumes late.
  useEffect(() => {
    const update = () => setNow(Date.now());
    const timer = setTimeout(
      update,
      Math.max(0, (expiresAt ?? Date.now()) - Date.now())
    );
    window.addEventListener("focus", update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", update);
    };
  }, [expiresAt]);
  return {
    rankedWallet:
      account && !seat.isError && expiresAt && expiresAt > now
        ? (seat.data?.rankedWallet ?? null)
        : null,
    named: Boolean(account && seat.data?.named),
    pending: Boolean(account && seat.isPending),
    revoke,
  };
}
